import { supabase } from "@/integrations/supabase/client";
import { ApiService } from "@/services/api/ApiService";
import { ImpersonationService } from "@/services/users/ImpersonationService";

export class ContentExtractionService {
  // ============================
  // Audio Upload
  // ============================

  static async transcribeAudio(audioFile: File, userId: string, language: string = 'en', agentId?: string) {
    try {
      const formData = new FormData();
      formData.append('action', 'audio');
      formData.append('file', audioFile);
      formData.append('userId', userId);
      formData.append('language', language);
      if (agentId) {
        formData.append('agentId', agentId);
      }
      const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
      if (impersonatedUserId) {
        formData.append('impersonatedUserId', impersonatedUserId);
      }

      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: formData
      });

      if (error) {
        console.error('Error transcribing audio:', error);
        throw new Error(error.message || 'Failed to transcribe audio');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to transcribe audio');
      }

      return data;
    } catch (error) {
      console.error('Error in transcribeAudio:', error);
      throw error;
    }
  }

  // ============================
  // Deep Research
  // ============================

  static async performDeepResearch(query: string, userId: string, title?: string, agentId?: string) {
    try {
      const data = await ApiService.callEdge('doc-upload', {
        action: 'deep-research',
        data: {
          query,
          userId,
          researchType: 'deep',
          title,
          agentId,
          impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined
        }
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to perform deep research');
      }

      return { document: data.document };
    } catch (error) {
      console.error('Error in performDeepResearch:', error);
      throw error;
    }
  }

  // ============================
  // Document Upload
  // ============================

  static async extractDocumentText(documentFile: File, userId: string, agentId?: string) {
    try {
      const formData = new FormData();
      formData.append('action', 'extract-document');
      formData.append('file', documentFile);
      formData.append('userId', userId);
      if (agentId) {
        formData.append('agentId', agentId);
      }
      const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
      if (impersonatedUserId) {
        formData.append('impersonatedUserId', impersonatedUserId);
      }

      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: formData
      });

      if (error) {
        console.error('Error extracting document text:', error);
        throw new Error(error.message || 'Failed to extract document text');
      }

      return data;
    } catch (error) {
      console.error('Error in extractDocumentText:', error);
      throw error;
    }
  }

  // ============================
  // Image Upload
  // ============================

  static async uploadImageFile(file: File, userId: string, agentId?: string) {
    try {
      const formData = new FormData();
      formData.append('action', 'image');
      formData.append('file', file);
      formData.append('userId', userId);
      if (agentId) {
        formData.append('agentId', agentId);
      }
      const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
      if (impersonatedUserId) {
        formData.append('impersonatedUserId', impersonatedUserId);
      }

      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: formData
      });

      if (error) {
        console.error('Error uploading image file:', error);
        throw new Error(error.message || 'Failed to upload image');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to upload image');
      }

      return data;
    } catch (error) {
      console.error('Error in uploadImageFile:', error);
      throw error;
    }
  }

  static async uploadImageFiles(fileUrls: string[], fileNames: string[], userId: string, agentId?: string) {
    try {
      const formData = new FormData();
      formData.append('action', 'upload-image-batch');
      formData.append('userId', userId);

      fileUrls.forEach(url => {
        formData.append('fileUrls[]', url);
      });
      fileNames.forEach(name => {
        formData.append('fileNames[]', name);
      });

      if (agentId) {
        formData.append('agentId', agentId);
      }
      const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
      if (impersonatedUserId) {
        formData.append('impersonatedUserId', impersonatedUserId);
      }

      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: formData
      });

      if (error) {
        console.error('Error uploading image files:', error);
        throw new Error(error.message || 'Failed to upload images');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to upload images');
      }

      return data;
    } catch (error) {
      console.error('Error in uploadImageFiles:', error);
      throw error;
    }
  }

  // ============================
  // Notes
  // ============================

  static async createNotesDocument(content: string, userId: string, title?: string, agentId?: string) {
    try {
      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: {
          action: 'create-notes',
          data: {
            content,
            userId,
            title,
            agentId,
            impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined
          }
        }
      });

      if (error) {
        console.error('Error creating notes document:', error);
        throw new Error(error.message || 'Failed to create notes document');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to create notes document');
      }

      return { document: data.document };
    } catch (error) {
      console.error('Error in createNotesDocument:', error);
      throw error;
    }
  }

  // ============================
  // PDF Upload
  // ============================

  static async uploadPdfFile(file: File, userId: string, agentId?: string) {
    try {
      const formData = new FormData();
      formData.append('action', 'upload-pdf');
      formData.append('file', file);
      formData.append('userId', userId);
      if (agentId) {
        formData.append('agentId', agentId);
      }
      const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
      if (impersonatedUserId) {
        formData.append('impersonatedUserId', impersonatedUserId);
      }

      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: formData
      });

      if (error) {
        console.error('Error uploading PDF file:', error);
        throw new Error(error.message || 'Failed to upload PDF');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to upload PDF');
      }

      return data;
    } catch (error) {
      console.error('Error in uploadPdfFile:', error);
      throw error;
    }
  }

  // ============================
  // PDF URL
  // ============================

  static async extractPdfFromUrl(pdfUrl: string, userId: string, agentId?: string) {
    try {
      const formData = new FormData();
      formData.append('fileUrl', pdfUrl);
      formData.append('userId', userId);
      if (agentId) {
        formData.append('agentId', agentId);
      }
      const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
      if (impersonatedUserId) {
        formData.append('impersonatedUserId', impersonatedUserId);
      }
      formData.append('action', 'upload-pdf');

      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: formData
      });

      if (error) {
        console.error('Error extracting PDF from URL:', error);
        throw new Error(error.message || 'Failed to extract PDF from URL');
      }

      return data;
    } catch (error) {
      console.error('Error in extractPdfFromUrl:', error);
      throw error;
    }
  }

  // ============================
  // Video Upload
  // ============================

  static async uploadVideoFromUrl(fileUrl: string, fileName: string, userId: string, agentId?: string) {
    try {
      const formData = new FormData();
      formData.append('fileUrl', fileUrl);
      formData.append('fileName', fileName);
      formData.append('userId', userId);
      if (agentId) {
        formData.append('agentId', agentId);
      }
      const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
      if (impersonatedUserId) {
        formData.append('impersonatedUserId', impersonatedUserId);
      }
      formData.append('action', 'upload-video');

      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: formData
      });

      if (error) {
        console.error('Error uploading video from URL:', error);
        throw new Error(error.message || 'Failed to upload video from URL');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to upload video from URL');
      }

      return data;
    } catch (error) {
      console.error('Error in uploadVideoFromUrl:', error);
      throw error;
    }
  }

  // For videos hosted externally (e.g. too large for Supabase Storage's 50MB limit).
  // The URL is passed straight through to the transcription service — never
  // downloaded into Supabase Storage or the edge function.
  static async extractVideoFromExternalUrl(videoUrl: string, userId: string, agentId?: string) {
    try {
      const data = await ApiService.callEdge('doc-upload', {
        action: 'upload-video',
        data: {
          fileUrl: videoUrl,
          isExternalUrl: true,
          userId,
          agentId,
          impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined,
        },
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to process video URL');
      }

      return data;
    } catch (error) {
      console.error('Error in extractVideoFromExternalUrl:', error);
      throw error;
    }
  }

  // ============================
  // Quick Research
  // ============================

  static async performQuickResearch(query: string, userId: string, title?: string, agentId?: string) {
    try {
      const data = await ApiService.callEdge('doc-upload', {
        action: 'quick-research',
        data: {
          query,
          userId,
          researchType: 'quick',
          title,
          agentId,
          impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined
        }
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to perform quick research');
      }

      return { document: data.document };
    } catch (error) {
      console.error('Error in performQuickResearch:', error);
      throw error;
    }
  }

  // ============================
  // Scrape Website
  // ============================

  static async scrapeWebsite(url: string, userId: string, maxCrawlDepth: number = 1, maxCrawlPages: number = 10, agentId?: string) {
    try {
      const { data, error } = await supabase.functions.invoke('doc-upload', {
        body: {
          action: 'scrape-website',
          data: {
            url,
            userId,
            maxCrawlDepth,
            maxCrawlPages,
            agentId,
            impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined
          }
        }
      });

      if (error) {
        console.error('Error scraping website:', error);
        throw new Error(error.message || 'Failed to scrape website');
      }

      return data;
    } catch (error) {
      console.error('Error in scrapeWebsite:', error);
      throw error;
    }
  }

  // ============================
  // Website
  // ============================

  static async extractWebsiteContent(url: string, userId: string, agentId?: string) {
    try {
      const data = await ApiService.callEdge('doc-upload', {
        action: 'upload-website',
        data: {
          url,
          userId,
          agentId,
          impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined
        }
      });

      return data;
    } catch (error) {
      console.error('Error in extractWebsiteContent:', error);
      throw error;
    }
  }

  static async crawlWebsiteBrightData(
    url: string,
    userId: string,
    maxDepth: number = 2,
    maxPages: number = 50,
    agentId?: string,
  ) {
    try {
      const data = await ApiService.callEdge('doc-upload', {
        action: 'crawl-website-brightdata',
        data: {
          url,
          userId,
          maxDepth,
          maxPages,
          agentId,
          impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined
        }
      });

      return data;
    } catch (error) {
      console.error('Error in crawlWebsiteBrightData:', error);
      throw error;
    }
  }

  static async extractWebsiteContentBrightData(url: string, userId: string, agentId?: string) {
    try {
      const data = await ApiService.callEdge('doc-upload', {
        action: 'upload-website-brightdata',
        data: {
          url,
          userId,
          agentId,
          impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined
        }
      });

      return data;
    } catch (error) {
      console.error('Error in extractWebsiteContentBrightData:', error);
      throw error;
    }
  }

  // ============================
  // YouTube
  // ============================

  static async extractYouTubeTranscript(youtubeUrl: string, userId: string, agentId?: string) {
    try {
      const videoIdMatch = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?\s]+)/);
      if (!videoIdMatch) {
        throw new Error('Invalid YouTube URL format');
      }
      const videoId = videoIdMatch[1].split('?')[0];

      const data = await ApiService.callEdge('doc-upload', {
        action: 'extract-youtube',
        data: {
          youtubeUrl,
          videoId,
          userId,
          agentId,
          impersonatedUserId: ImpersonationService.getImpersonatedUserId() ?? undefined
        }
      });

      return data;
    } catch (error) {
      console.error('Error in extractYouTubeTranscript:', error);
      throw error;
    }
  }
}